import createNamedContext from 'utils/createNamedContext';
import { useContext, ProviderProps, useMemo } from 'react';
import { useRequest } from 'ahooks';
import { useTranslate } from '@tolgee/react';
import { message } from 'antd';
import { getTaskPolicyType } from './api';
import { reduce, uniqBy } from 'lodash-es';
import { useTypeLabel } from '../../Todo/hooks/useLabel';

type CustomType = {
  types: {
    name: string;
    id: number;
  }[];
  typeMap: Record<string, string>;
};

const CustomTypeContext = createNamedContext<CustomType | null>('CustomTypeContext', null);

export const useCustomTypeContext = () => {
  const value = useContext(CustomTypeContext);
  if (value === null) {
    throw new Error(
      'useCustomTypeContext hook should be used in a context of CustomTypeContextProvider component',
    );
  }
  return value;
};

export const CustomTypeContextProvider = ({
  children,
}: Omit<ProviderProps<CustomType>, 'value'>) => {
  const { t } = useTranslate();
  const { data } = useRequest(() => getTaskPolicyType(), {
    onError: () => {
      message.error(t('phrase.获取数据失败，请稍后再试'));
    },
  });
  const { getTypeLabel } = useTypeLabel();
  const types = useMemo(() => {
    return reduce(
      uniqBy(data, 'type'),
      (acc, cur) => {
        acc.push({
          name: cur.source === 1 ? cur.name : (getTypeLabel(cur.type) as string),
          id: cur.type,
        });
        return acc;
      },
      [] as CustomType['types'],
    );
  }, [data, getTypeLabel]);
  const typeMap = useMemo(() => {
    return types.reduce((acc, cur) => {
      acc[cur.id] = cur.name;
      return acc;
    }, {} as CustomType['typeMap']);
  }, [types]);
  return (
    <CustomTypeContext.Provider
      value={{
        types,
        typeMap,
      }}
    >
      {children}
    </CustomTypeContext.Provider>
  );
};
